import {
  rrcProfileId,
  rrcProfileKey,
  type RrcProfileId,
  type RrcProfileKey,
} from '../rrc-profile-query';
import { buildStandardHeaders, otsBaseApi } from '../../ots-api';
import {
  RrcEp9WasteProfile,
  RrcEp9WasteProfileCreateDTO,
  RrcEp9WasteProfileUpdateDTO,
} from '../../../model/rrc/rrc-ep9-waste-profile';
import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { OtsAsyncThunkConfigExtra } from '../../ots-store';

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: [
    'RrcEp9WasteProfiles',
    'ObjectRrcEp9WasteProfiles',
    'KeyRrcEp9WasteProfiles',
    'CurrentRrcEp9WasteProfile',
    'ObjectObjectChangeLogs',
  ],
});

export const rrcEp9WasteProfileApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentRrcEp9WasteProfilesByEp9Waste: builder.query<
      RrcEp9WasteProfile[],
      { objectId: string; objectType: string }
    >({
      query: (arg) => ({
        url: '/compliance/rrc/ep9/current',
        params: { objectId: arg.objectId, objectType: arg.objectType },
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'RrcEp9WasteProfiles', id: 'LIST' },
        { type: 'ObjectRrcEp9WasteProfiles', id: arg.objectId },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcEp9WasteProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcEp9WasteProfilesByEp9Waste: builder.query<
      RrcEp9WasteProfile[],
      { objectId: string; objectType: string }
    >({
      query: (arg) => ({
        url: '/compliance/rrc/ep9',
        params: { objectId: arg.objectId, objectType: arg.objectType },
      }),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'RrcEp9WasteProfiles', id: 'LIST' },
        { type: 'ObjectRrcEp9WasteProfiles', id: arg.objectId },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcEp9WasteProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcEp9WasteProfilesByKey: builder.query<
      RrcEp9WasteProfile[],
      RrcProfileKey
    >({
      query: (arg) => ({
        url: `/compliance/rrc/ep9/by-key/${encodeURIComponent(
          rrcProfileKey(arg).profileKey
        )}/versions`,
        params: { tenantCode: rrcProfileKey(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileKey(queryArgs),
      keepUnusedDataFor: 120,
      providesTags: (result, error, arg) => [
        { type: 'KeyRrcEp9WasteProfiles', id: rrcProfileKey(arg).profileKey },
        ...(result ?? []).map(({ id }) => ({
          type: 'RrcEp9WasteProfiles' as const,
          id,
        })),
      ],
    }),
    getRrcEp9WasteProfile: builder.query<RrcEp9WasteProfile, RrcProfileId>({
      query: (arg) => ({
        url: `/compliance/rrc/ep9/${encodeURIComponent(rrcProfileId(arg).id)}`,
        params: { tenantCode: rrcProfileId(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileId(queryArgs),
      providesTags: (result, error, arg) => [
        { type: 'RrcEp9WasteProfiles', id: rrcProfileId(arg).id },
      ],
    }),
    getCurrentRrcEp9WasteProfileByKey: builder.query<
      RrcEp9WasteProfile,
      RrcProfileKey
    >({
      query: (arg) => ({
        url: `/compliance/rrc/ep9/by-key/${encodeURIComponent(
          rrcProfileKey(arg).profileKey
        )}`,
        params: { tenantCode: rrcProfileKey(arg).tenantCode },
      }),
      serializeQueryArgs: ({ queryArgs }) => rrcProfileKey(queryArgs),
      providesTags: (result, error, arg) => [
        {
          type: 'CurrentRrcEp9WasteProfile',
          id: rrcProfileKey(arg).profileKey,
        },
        { type: 'KeyRrcEp9WasteProfiles', id: rrcProfileKey(arg).profileKey },
        ...(result
          ? [{ type: 'RrcEp9WasteProfiles' as const, id: result.id }]
          : []),
      ],
    }),
    createRrcEp9WasteProfile: builder.mutation<
      RrcEp9WasteProfile,
      RrcEp9WasteProfileCreateDTO
    >({
      query: (arg) => ({
        url: '/compliance/rrc/ep9',
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'RrcEp9WasteProfiles', id: 'LIST' },
            { type: 'KeyRrcEp9WasteProfiles', id: result.profileKey },
            { type: 'ObjectRrcEp9WasteProfiles', id: result.objectId },
            { type: 'CurrentRrcEp9WasteProfile' },
          ];
        } else {
          return [
            { type: 'RrcEp9WasteProfiles', id: 'LIST' },
            { type: 'KeyRrcEp9WasteProfiles' },
            { type: 'ObjectRrcEp9WasteProfiles' },
            { type: 'CurrentRrcEp9WasteProfile' },
          ];
        }
      },
    }),
    updateRrcEp9WasteProfile: builder.mutation<
      RrcEp9WasteProfile,
      RrcEp9WasteProfileUpdateDTO
    >({
      query: (arg) => ({
        url: '/compliance/rrc/ep9',
        method: 'PUT',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        if (result) {
          return [
            { type: 'RrcEp9WasteProfiles', id: 'LIST' },
            { type: 'KeyRrcEp9WasteProfiles', id: result.profileKey },
            { type: 'ObjectRrcEp9WasteProfiles', id: result.objectId },
            { type: 'CurrentRrcEp9WasteProfile' },
          ];
        } else {
          return [
            { type: 'RrcEp9WasteProfiles', id: 'LIST' },
            { type: 'KeyRrcEp9WasteProfiles' },
            { type: 'ObjectRrcEp9WasteProfiles' },
            { type: 'CurrentRrcEp9WasteProfile' },
          ];
        }
      },
    }),
    deleteRrcEp9WasteProfile: builder.mutation<
      void,
      Partial<RrcEp9WasteProfile>
    >({
      query: (arg) => ({
        url: '/compliance/rrc/ep9',
        method: 'DELETE',
        body: arg,
      }),
      invalidatesTags: (result, error, args) => {
        return [
          { type: 'RrcEp9WasteProfiles', id: 'LIST' },
          { type: 'KeyRrcEp9WasteProfiles' },
          { type: 'ObjectRrcEp9WasteProfiles' },
          { type: 'CurrentRrcEp9WasteProfile' },
        ];
      },
    }),
  }),
});

export const {
  useGetCurrentRrcEp9WasteProfilesByEp9WasteQuery,
  useGetRrcEp9WasteProfilesByEp9WasteQuery,
  useGetRrcEp9WasteProfilesByKeyQuery,
  useGetRrcEp9WasteProfileQuery,
  useGetCurrentRrcEp9WasteProfileByKeyQuery,
  useCreateRrcEp9WasteProfileMutation,
  useUpdateRrcEp9WasteProfileMutation,
  useDeleteRrcEp9WasteProfileMutation,
} = rrcEp9WasteProfileApi;

export const downloadRrcEp9WasteProfile = (
  id: string,
  api: BaseQueryApi
): Promise<Response> => {
  const headers = buildStandardHeaders(api);
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  const request: RequestInit = {
    method: 'GET',
    headers: headers,
  };

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;

  const url = `${baseUrl}/compliance/rrc/ep9/${id}/download/pdf`;

  return window.fetch(url, request).then((response) => {
    if (response.ok) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  });
};
